import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { ListTodo, Timer, Workflow } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroImg from "@/assets/pipelines-hero.webp";
import boardImg from "@/assets/pipelines-board.webp";
import autoImg from "@/assets/pipelines-auto.webp";

const IMG: Record<string, string> = { hero: heroImg, board: boardImg, auto: autoImg };

export const Route = createFileRoute("/platform/pipelines")({
  head: () => ({
    meta: [
      { title: "Pipelines & Tasks — banana.cards" },
      { name: "description", content: "Build pipelines, assign tasks, automate next steps — close deals faster." },
      { property: "og:title", content: "Pipelines & Tasks — banana.cards" },
      { property: "og:description", content: "Build pipelines, assign tasks, automate next steps — close deals faster." },
      { property: "og:image", content: IMG['hero'] },
    ],
    links: alternateLinkList("/platform/pipelines", "en"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Pipelines & Tasks"
      title="Move every deal"
      italic="forward."
      description="Build pipelines that match the way you actually sell. Drag deals between stages, assign tasks, set reminders, and let automations push the rest forward."
      heroImage={IMG['hero']}
      heroImageAlt="Sales pipeline board on banana.cards with deals moving through Discovery, Proposal and Won stages"
      metrics={[{ v: "Unlimited", l: "pipelines" }, { v: "Auto", l: "next-step" }, { v: "3x", l: "throughput" }]}
      bullets={[
        { icon: Workflow, title: "Custom pipelines", desc: "Per team, per product, per region — and switch views in a click." },
        { icon: ListTodo, title: "Tasks that nag", desc: "Auto-assigned, auto-due, with reminders that actually fire." },
        { icon: Timer, title: "SLA tracking", desc: "See where deals stall and where reps need help." }
      ]}
      deepDives={[
        { eyebrow: "Board", title: "Drag, drop, done.", italic: "Kanban or list.", description: "Switch between board, list and timeline views without losing context. Bulk-edit hundreds of deals in seconds.", imageAlt: "Kanban sales pipeline with drag-and-drop deals, bulk edit and custom deal fields", image: IMG["board"], reverse: false, points: ["Kanban, list and timeline views", "Bulk edit and assign", "Custom deal fields and formulas", "Conditional stage requirements"] },
        { eyebrow: "Automations", title: "Move work", italic: "while you sleep.", description: "When a deal hits 'proposal sent', auto-create a follow-up task, schedule a reminder and notify the rep — all from one trigger.", imageAlt: "Pipeline automation triggering follow-up tasks, calendar reminders and Slack notifications on stage change", image: IMG["auto"], reverse: true, points: ["Triggers on stage change", "Auto-task creation", "Slack / email notifications", "Round-robin assignment"] }
      ]}
      howItWorks={[
        { title: "Design your stages", desc: "Match how you actually sell." },
        { title: "Add deals", desc: "From contacts, forms or imports." },
        { title: "Automate the boring", desc: "Tasks, reminders, notifications." },
        { title: "Track and coach", desc: "SLA, velocity, stalled deals." }
      ]}
      capabilities={[
        "Unlimited pipelines",
        "Custom stages and fields",
        "Tasks with SLAs",
        "Round-robin assignment",
        "Stage requirements",
        "Bulk actions",
        "Velocity & funnel reports",
        "API & webhooks"
      ]}
      faq={[
        { q: "What can I actually track in a pipeline?", a: "Anything that moves through stages: sales deals and opportunities, client onboarding, hiring (candidates → offer → hired), project delivery, partnership negotiations, support tickets, even content production. Each pipeline has fully custom stages, custom fields per card, and a deal value with currency — so the same workspace can run a Sales pipeline and a Hiring pipeline side by side." },
        { q: "How many pipelines can I create and how big can they get?", a: "Create as many pipelines as you need across your plan limit, with unlimited stages and unlimited cards per stage. Drag-and-drop kanban handles thousands of cards smoothly thanks to virtualized rendering." },
        { q: "Can I attach contacts, tasks and notes to a deal card?", a: "Yes. Every card links to one or more CRM contacts, carries its own task list with due dates and assignees, supports notes and file attachments, and shows a full activity timeline. Closing the card writes the outcome back to each linked contact." },
        { q: "Can I forecast revenue and weight deals?", a: "Yes — assign a value and currency to each card, set a probability per stage (so 'Negotiation' might be 70%) and Banana rolls up weighted forecast totals per pipeline, per assignee and per time window. FX conversion to your account currency happens automatically." },
        { q: "Does it work in multiple currencies?", a: "Yes — record each deal in its native currency (USD, EUR, GBP, VND, etc.) and Banana converts to your workspace's reporting currency using live FX rates for forecast roll-ups." },
        { q: "Can I round-robin assign new deals across the team?", a: "Yes — set a default assignee per pipeline or round-robin across a group, and trigger automations on stage change (e.g. when a deal hits 'Won', auto-create the onboarding contract and a kickoff task)." }
      ]}
      outro={ { title: "Stop chasing.", italic: "Start closing.", description: "Pipelines and tasks that actually move work forward." } }
    />
  );
}
